// src/common/services/motolivraison/motolivraison.service.js
//
// Service API du module MotoLivraison côté backoffice.
// Toutes les méthodes admin pour gérer catégories, commerces, livreurs, commandes, reversements, stats.
// Routes serveur préfixées par /admin/motolivraison/*

import { exeRequest } from '../../utils/APIUtils';

// eslint-disable-next-line import/no-anonymous-default-export
export default {
    // ============ CATÉGORIES ============
    listCategories: async (onlyActive = false) =>
        exeRequest(`/motolivraison/admin/categories${onlyActive ? '?onlyActive=true' : ''}`, 'GET'),

    getCategorieById: async (id) =>
        exeRequest(`/motolivraison/admin/categories/${id}`, 'GET'),

    createCategorie: async (data) =>
        exeRequest(`/motolivraison/admin/categories`, 'POST', data),

    updateCategorie: async (id, data) =>
        exeRequest(`/motolivraison/admin/categories/${id}`, 'PUT', data),

    toggleActiveCategorie: async (id) =>
        exeRequest(`/motolivraison/admin/categories/${id}/toggle-active`, 'PATCH'),

    deleteCategorie: async (id) =>
        exeRequest(`/motolivraison/admin/categories/${id}`, 'DELETE'),

    // ============ COMMERÇANTS (inscriptions utilisateur) ============
    listCommercants: async (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([_, v]) => v != null && v !== '')
        ).toString();
        return exeRequest(`/motolivraison/admin/commercants${qs ? '?' + qs : ''}`, 'GET');
    },

    getCommercantById: async (id) =>
        exeRequest(`/motolivraison/admin/commercants/${id}`, 'GET'),

    validateCommercant: async (id) =>
        exeRequest(`/motolivraison/admin/commercants/${id}/validate`, 'PUT'),

    suspendCommercant: async (id) =>
        exeRequest(`/motolivraison/admin/commercants/${id}/suspend`, 'PUT'),

    reactivateCommercant: async (id) =>
        exeRequest(`/motolivraison/admin/commercants/${id}/reactivate`, 'PUT'),

    // ============ COMMERCES ============
    listCommerces: async (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([_, v]) => v != null && v !== '')
        ).toString();
        return exeRequest(`/motolivraison/admin/commerces${qs ? '?' + qs : ''}`, 'GET');
    },

    getCommerceById: async (id) =>
        exeRequest(`/motolivraison/admin/commerces/${id}`, 'GET'),

    getCommerceArticles: async (id) =>
        exeRequest(`/motolivraison/admin/commerces/${id}/articles`, 'GET'),

    validateCommerce: async (id) =>
        exeRequest(`/motolivraison/admin/commerces/${id}/validate`, 'PUT'),

    suspendCommerce: async (id) =>
        exeRequest(`/motolivraison/admin/commerces/${id}/suspend`, 'PUT'),

    rejectCommerce: async (id, reason) =>
        exeRequest(`/motolivraison/admin/commerces/${id}/reject`, 'PUT', { reason }),

    reactivateCommerce: async (id) =>
        exeRequest(`/motolivraison/admin/commerces/${id}/reactivate`, 'PUT'),

    /// Corrige la ville d'un commerce — Body : { cityCode }
    updateCommerceCity: async (id, cityCode) =>
        exeRequest(`/motolivraison/admin/commerces/${id}/city`, 'PUT', { cityCode }),

    /// Modifie la fiche d'un commerce (admin pour le compte du commerçant).
    /// `data` : objet JSON, ou FormData si un nouveau logo est envoyé (champ 'image').
    updateCommerce: async (id, data) =>
        exeRequest(`/motolivraison/admin/commerces/${id}`, 'PUT', data),

    // ============ ARTICLES (catalogue saisi par l'admin) ============
    /// Ajoute un article au catalogue d'un commerce.
    /// `data` : objet JSON, ou FormData si une photo est envoyée (champ 'image').
    createArticle: async (commerceId, data) =>
        exeRequest(`/motolivraison/admin/commerces/${commerceId}/articles`, 'POST', data),

    updateArticle: async (articleId, data) =>
        exeRequest(`/motolivraison/admin/articles/${articleId}`, 'PUT', data),

    toggleArticleActive: async (articleId) =>
        exeRequest(`/motolivraison/admin/articles/${articleId}/toggle-active`, 'PATCH'),

    deleteArticle: async (articleId) =>
        exeRequest(`/motolivraison/admin/articles/${articleId}`, 'DELETE'),

    /// Crée un commerçant directement (mot de passe par défaut 123456)
    /// Body : { phone, firstName, lastName, email?, cityCode?, documentUrl? }
    createCommercantByAdmin: async (data) =>
        exeRequest(`/motolivraison/admin/commerces/create-commercant`, 'POST', data),

    // ============ LIVREURS ============
    listLivreurs: async (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([_, v]) => v != null && v !== '')
        ).toString();
        return exeRequest(`/motolivraison/admin/livreurs${qs ? '?' + qs : ''}`, 'GET');
    },

    getLivreurById: async (id) =>
        exeRequest(`/motolivraison/admin/livreurs/${id}`, 'GET'),

    validateLivreur: async (id) =>
        exeRequest(`/motolivraison/admin/livreurs/${id}/validate`, 'PUT'),

    suspendLivreur: async (id) =>
        exeRequest(`/motolivraison/admin/livreurs/${id}/suspend`, 'PUT'),

    reactivateLivreur: async (id) =>
        exeRequest(`/motolivraison/admin/livreurs/${id}/reactivate`, 'PUT'),

    /// Corrige la ville d'affectation d'un livreur (pilote le dispatch) — Body : { cityCode }
    updateLivreurCity: async (id, cityCode) =>
        exeRequest(`/motolivraison/admin/livreurs/${id}/city`, 'PUT', { cityCode }),

    /// Recharge (ou retire) des points au livreur — tracé côté serveur.
    /// Body : { montant: Number (>0 crédite, <0 retire), motif?: String }
    rechargeLivreur: async (id, data) =>
        exeRequest(`/motolivraison/admin/livreurs/${id}/recharge`, 'PUT', data),

    /// Crée un livreur directement (mot de passe par défaut 123456)
    /// Body : { phone, firstName, lastName, email?, cityCode?, permisUrl?, photoMotoUrl? }
    createLivreurByAdmin: async (data) =>
        exeRequest(`/motolivraison/admin/livreurs/create-livreur`, 'POST', data),

    // ============ COMMANDES ============
    listCommandes: async (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([_, v]) => v != null && v !== '')
        ).toString();
        return exeRequest(`/motolivraison/admin/commandes${qs ? '?' + qs : ''}`, 'GET');
    },

    getCommandeById: async (id) =>
        exeRequest(`/motolivraison/admin/commandes/${id}`, 'GET'),

    assignerLivreur: async (commandeId, livreurId) =>
        exeRequest(`/motolivraison/admin/commandes/${commandeId}/assign`, 'PUT', { livreurId }),

    cancelCommande: async (commandeId, reason) =>
        exeRequest(`/motolivraison/admin/commandes/${commandeId}/cancel`, 'PUT', { reason }),

    /// Fait avancer une commande bloquée. Body : { statut?, motif }
    /// Le motif est obligatoire côté serveur : il justifie l'intervention.
    avancerCommande: async (id, data) =>
        exeRequest(`/motolivraison/admin/commandes/${id}/avancer`, 'PUT', data),

    // ============ REVERSEMENTS ============
    listReversements: async (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([_, v]) => v != null && v !== '')
        ).toString();
        return exeRequest(`/motolivraison/admin/reversements${qs ? '?' + qs : ''}`, 'GET');
    },

    getReversementById: async (id) =>
        exeRequest(`/motolivraison/admin/reversements/${id}`, 'GET'),

    previewCommandesNonReversees: async (commerceId, periodeDebut, periodeFin) => {
        const qs = new URLSearchParams(
            Object.entries({ periodeDebut, periodeFin }).filter(([_, v]) => v != null && v !== '')
        ).toString();
        return exeRequest(
            `/motolivraison/admin/reversements/commerces/${commerceId}/commandes-non-reversees${qs ? '?' + qs : ''}`,
            'GET'
        );
    },

    createReversement: async (data) =>
        exeRequest(`/motolivraison/admin/reversements`, 'POST', data),

    payReversement: async (id, referenceExterne) =>
        exeRequest(`/motolivraison/admin/reversements/${id}/pay`, 'PUT', { referenceExterne }),

    cancelReversement: async (id, reason) =>
        exeRequest(`/motolivraison/admin/reversements/${id}/cancel`, 'PUT', { reason }),

    // ============ STATS ============
    getStats: async (periode = 'monthly') =>
        exeRequest(`/motolivraison/admin/stats?periode=${periode}`, 'GET'),
};
